import * as React from "react";
import { FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME } from "@/components/chat/SidebarChat/franklinUpdateVisual";
import { cn } from "@/lib/utils";

/**
 * Toast primitives, reduced to the one piece call sites still use.
 *
 * The app renders a single toaster — sonner (`components/ui/sonner.tsx`) — so
 * the Radix toast viewport/root/title/description/close primitives are gone
 * along with the `@radix-ui/react-toast` dependency. `ToastAction` survives as
 * a plain button because sonner renders a React element `action` verbatim, and
 * a handful of call sites pass one. Sonner's `actionButton` class never reaches
 * a verbatim element, so this button carries the toaster's primary-action look
 * and its grid cell (row 2, under the message) itself.
 */
export interface ToastActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Screen-reader label for the action, mirroring the Radix API. */
  altText?: string;
}

const ToastAction = React.forwardRef<HTMLButtonElement, ToastActionProps>(
  ({ className, altText, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={altText}
      className={cn(
        FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME,
        "col-start-2 row-start-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      {...props}
    />
  )
);
ToastAction.displayName = "ToastAction";

export type ToastActionElement = React.ReactElement<ToastActionProps>;

/** Shape a `toast()` call may carry beyond title/description. */
export interface ToastProps {
  variant?: "default" | "destructive";
  duration?: number;
}

export { ToastAction };
